import React, { useEffect, useState } from "react";
import styles from "./OrderProcessingPage.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  selectIsAuthenticated,
  selectIsAdmin,
  selectIsDriver,
} from "../../store/auth-slice";
import ReceivedColumn from "../../components/OrderProcessingComponents/Columns/ReceivedColumn/ReceivedColumn";
import ProcessedColumn from "../../components/OrderProcessingComponents/Columns/ProcessedColumn/ProcessedColumn";
import ArchivedColumn from "../../components/OrderProcessingComponents/Columns/ArchiveColumn/ArchivedColumn";
import {
  fetchAllOrdersForAdminUser,
  fetchAllOrdersForDriverUser,
  fetchAllTransactionsForAdminUser,
  fetchOrders,
  fetchTransactions,
} from "../../store/order-actions";
import {
  fetchAllRFZOTherapyRequests,
  fetchRFZOTherapyRequests,
} from "../../store/rfzoTherapy-actions";
import { orderActions } from "../../store/order-slice";
import PendingColumnDriver from "../../components/OrderProcessingComponents/Columns/PendingColumnDriver/PendingColumnDriver";
import Tabs from "../../components/UI/Tabs/Tabs";
import DriverArchivedColumn from "../../components/OrderProcessingComponents/Columns/DriverArchievedColumn/DriverArchievedColumn";

const OrderProcessingPage = () => {
  const dispatch = useDispatch();
  const rfzoRequest = useSelector(
    (state: RootState) => state.rfzoTherapy.rfzoRequest
  );
  const marketplaceInfo = useSelector(
    (state: RootState) => state.auth.marketplaceInfo
  );

  // Use Redux selectors instead of local state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  const isDriver = useSelector(selectIsDriver);
  const isPharmacist = useSelector(
    (state: RootState) => state.auth.isPharmacist
  );

  const [marketPlaceInfoFetched, setMarketPlaceInfoFetched] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      if (!isPharmacist) {
        if (isAdmin) {
          dispatch<any>(fetchAllOrdersForAdminUser());
          dispatch<any>(fetchAllTransactionsForAdminUser());
          dispatch<any>(fetchAllRFZOTherapyRequests());
        } else if (isDriver) {
          dispatch<any>(fetchAllOrdersForDriverUser());
        } else {
          if (marketPlaceInfoFetched && marketplaceInfo !== null) {
            dispatch<any>(
              fetchOrders(
                marketplaceInfo.branchMarketplaceId,
                marketplaceInfo.marketplaceId
              )
            );
            dispatch<any>(
              fetchTransactions(
                marketplaceInfo.branchMarketplaceId,
                marketplaceInfo.marketplaceId
              )
            );
            dispatch<any>(
              fetchRFZOTherapyRequests(
                marketplaceInfo.branchMarketplaceId,
                marketplaceInfo.marketplaceId
              )
            );
          }
        }
      }
    }
    return () => {
      dispatch(orderActions.clearOrdersAndTransactions());
    };
  }, [
    isAuthenticated,
    isAdmin,
    isDriver,
    isPharmacist,
    marketPlaceInfoFetched,
    marketplaceInfo,
    dispatch,
  ]);

  useEffect(() => {
    if (isDriver) {
      const interval = setInterval(() => {
        console.log("isDriver");
        dispatch<any>(fetchAllOrdersForDriverUser());
      }, 10000);
      return () => clearInterval(interval);
    }
    // Regular marketplace user (non-admin, non-pharmacist, non-driver)
    if (!isAdmin && !isPharmacist && !isDriver && marketplaceInfo) {
      const interval = setInterval(() => {
        dispatch<any>(
          fetchOrders(
            marketplaceInfo.branchMarketplaceId,
            marketplaceInfo.marketplaceId,
            false
          )
        );
        dispatch<any>(
          fetchTransactions(
            marketplaceInfo.branchMarketplaceId,
            marketplaceInfo.marketplaceId
          )
        );
        dispatch<any>(
          fetchRFZOTherapyRequests(
            marketplaceInfo.branchMarketplaceId,
            marketplaceInfo.marketplaceId
          )
        );
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, isPharmacist, isDriver, marketplaceInfo, dispatch]);

  useEffect(() => {
    if (marketplaceInfo) {
      setMarketPlaceInfoFetched(true);
    } else {
      setMarketPlaceInfoFetched(false);
    }
  }, [marketplaceInfo]);

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      {isMobile && (
        <>
          {isDriver ? (
            <DriverTabs
              onChange={(tab) => setSelectedTab(tab)}
              selected={selectedTab}
            />
          ) : (
            <MerchantTabs
              onChange={(tab) => setSelectedTab(tab)}
              selected={selectedTab}
            />
          )}
        </>
      )}
      <div className={styles.OrderProcessingPage}>
        {!isDriver ? (
          <>
            {((isMobile && selectedTab === 0) || !isMobile) && (
              <ReceivedColumn isAdmin={isAdmin} />
            )}
            {((isMobile && selectedTab === 1) || !isMobile) && (
              <ProcessedColumn title="Spakovane" isAdmin={isAdmin} />
            )}
            {((isMobile && selectedTab === 2) || !isMobile) && (
              <ArchivedColumn isAdmin={isAdmin} />
            )}
          </>
        ) : (
          <>
            {((isMobile && selectedTab === 0) || !isMobile) && (
              <PendingColumnDriver isDriver={true} isAdmin={isAdmin} />
            )}
            {((isMobile && selectedTab === 1) || !isMobile) && (
              <ProcessedColumn
                title="Preuzete"
                isDriver={isDriver}
                isAdmin={isAdmin}
              />
            )}
            {((isMobile && selectedTab === 2) || !isMobile) && (
              <DriverArchivedColumn isAdmin={isAdmin} />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default OrderProcessingPage;

type Props = {
  selected: number;
  onChange: (tab: any) => any;
};

const MerchantTabs = ({ onChange, selected }: Props) => {
  const orderState = useSelector((state: RootState) => state.order);
  const rfzoTherapy = useSelector((state: RootState) => state.rfzoTherapy);

  const rfzoRequestRequested = rfzoTherapy.rfzoTherapyRequests.filter(
    (rfzoRequest) => {
      return rfzoRequest.status === "REQUESTED";
    }
  );

  const rfzoRequestNotRequested = rfzoTherapy.rfzoTherapyRequests.filter(
    (rfzoRequest) => {
      return rfzoRequest.status !== "REQUESTED";
    }
  );

  const pedningCount =
    orderState.receivedOrders.length + rfzoRequestRequested.length;
  const processedCount = orderState.processedOrders.length.toString();
  const archievedCount =
    orderState.finishedOrders.length + rfzoRequestNotRequested.length;

  const tabsList = [
    {
      label: "Pristiglih",
      count: pedningCount,
    },
    {
      label: "Spakovane",
      count: processedCount,
    },
    {
      label: "Obradjenih",
      count: archievedCount,
    },
  ];

  return <Tabs onChange={onChange} selected={selected} tabsList={tabsList} />;
};
const DriverTabs = ({ onChange, selected }: Props) => {
  const orderState = useSelector((state: RootState) => state.order);
  const rfzoTherapy = useSelector((state: RootState) => state.rfzoTherapy);

  const rfzoRequestRequested = rfzoTherapy.rfzoTherapyRequests.filter(
    (rfzoRequest) => {
      return rfzoRequest.status === "REQUESTED";
    }
  );

  const pedningCount =
    orderState.receivedOrders.length + rfzoRequestRequested.length;
  const processedCount = orderState.processedOrders.length.toString();
  const archievedCount =
    orderState.finishedOrders.length + rfzoRequestRequested.length;

  const tabsList = [
    {
      label: "Pristiglih",
      count: pedningCount,
    },
    {
      label: "Preuzetih",
      count: processedCount,
    },
    {
      label: "Predatih",
      count: archievedCount,
    },
  ];

  return <Tabs onChange={onChange} selected={selected} tabsList={tabsList} />;
};
